/**
 * Calendar-grid maths, done on strings.
 *
 * Extracted from the GTM content calendar, which had its own copies of `iso` and
 * `mondayOf`. Deliberately no date library: the whole job is four operations, and
 * the bug this codebase actually suffers from is the UTC day shift — which is
 * fixed by *avoiding* `Date` for calendar days, not by wrapping it.
 */

export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

/**
 * A Date as a local YYYY-MM-DD.
 *
 * Must NOT use `toISOString()` — that is UTC, and west of it (or east, in the
 * evening) it yields the wrong day.
 */
export function isoLocal(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Monday of the week containing `d`, at local midnight. */
export function mondayOf(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  const dayOfWeek = (out.getDay() + 6) % 7; // Mon=0 … Sun=6
  out.setDate(out.getDate() - dayOfWeek);
  return out;
}

/** Add days to a YYYY-MM-DD. Pure string maths — no offset to get wrong. */
export function addDaysIso(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Whole days from `a` to `b`. Negative when `b` precedes `a`. */
export function daysBetweenIso(a: string, b: string): number {
  const from = new Date(`${a}T00:00:00Z`).getTime();
  const to = new Date(`${b}T00:00:00Z`).getTime();
  return Math.round((to - from) / 86_400_000);
}

export interface MonthGrid {
  /** Every day in the grid, Monday-first, 35 or 42 entries. */
  days: string[];
  from: string;
  to: string;
  /** The month the grid is anchored on, for dimming the spill-over days. */
  month: number;
  year: number;
}

/**
 * A Monday-first grid covering the month containing `anchor`.
 *
 * Six rows when the month needs them, five otherwise — so February starting on a
 * Monday does not render a blank trailing week.
 */
export function monthGridDays(anchor: Date): MonthGrid {
  const year = anchor.getFullYear();
  const month = anchor.getMonth();

  const firstOfMonth = new Date(year, month, 1);
  const lastOfMonth = new Date(year, month + 1, 0);
  const gridStart = mondayOf(firstOfMonth);

  const spanDays = daysBetweenIso(isoLocal(gridStart), isoLocal(lastOfMonth)) + 1;
  const weeks = Math.ceil(spanDays / 7);

  const days: string[] = [];
  const start = isoLocal(gridStart);
  for (let i = 0; i < weeks * 7; i += 1) days.push(addDaysIso(start, i));

  return {
    days,
    from: days[0]!,
    to: days[days.length - 1]!,
    month: month + 1,
    year,
  };
}

/** 'August 2026' */
export function monthLabel(anchor: Date): string {
  return new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' }).format(anchor);
}

/** The month a YYYY-MM-DD belongs to, for dimming days outside the grid's month. */
export function monthOfIso(iso: string): number {
  return Number(iso.slice(5, 7));
}

/** 'Fri 27 Sep' — for a day-detail heading. */
export function dayHeading(iso: string): string {
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date(`${iso}T12:00:00Z`));
}
